import React, { useEffect, useRef, useState } from 'react';
import { createChart, LineSeries } from 'lightweight-charts';

const ChartCard = ({ loadingTime, lastLoaded, data, timeframe, currentFrames = 100, onFetchFrames }) => {
  const chartContainerRef = useRef(null);
  const chartRef = useRef(null);
  
  const seriesRefs = useRef({ sma20: null, sma50: null, sma200: null });
  const [framesInput, setFramesInput] = useState(currentFrames);

  const [visibility, setVisibility] = useState({
    sma20: true,
    sma50: true,
    sma200: true
  });

  useEffect(() => {
    setFramesInput(currentFrames);
  }, [currentFrames]);

  useEffect(() => {
    if (seriesRefs.current.sma20) seriesRefs.current.sma20.applyOptions({ visible: visibility.sma20 });
    if (seriesRefs.current.sma50) seriesRefs.current.sma50.applyOptions({ visible: visibility.sma50 });
    if (seriesRefs.current.sma200) seriesRefs.current.sma200.applyOptions({ visible: visibility.sma200 });
  }, [visibility]);

  useEffect(() => {
    if (!chartContainerRef.current) return;

    chartContainerRef.current.innerHTML = '';

    const chart = createChart(chartContainerRef.current, {
      width: chartContainerRef.current.clientWidth,
      height: 400,
      layout: {
        background: { type: 'solid', color: '#000000' }, // Pure Black
        textColor: '#7c8a9e', 
      },
      grid: {
        // Extra faint grid lines
        vertLines: { color: '#383636' }, 
        horzLines: { color: '#383636' },
      },
      crosshair: { 
        mode: 0,
        vertLine: { color: '#383636', labelBackgroundColor: '#111111' },
        horzLine: { color: '#383636', labelBackgroundColor: '#111111' }
      },
      rightPriceScale: {
        borderColor: '#111111', // Faint border
        autoScale: true, 
        scaleMargins: { top: 0.05, bottom: 0.05 },
      },
      timeScale: {
        borderColor: '#111111', // Faint border
        timeVisible: true,
        secondsVisible: false,
        fixLeftEdge: true, 
        fixRightEdge: true,
      },
      handleScale: {
        axisPressedMouseMove: { price: false, time: true },
      },
      handleScroll: { vertTouchDrag: false }
    });

    const fixedScaleProvider = () => ({
      priceRange: { minValue: 0, maxValue: 100 },
      margins: { above: 0, below: 0 }
    });

    // Data lines made slightly fainter/thinner (lineWidth: 1.5)
    const sma20Series = chart.addSeries(LineSeries, {
      color: '#84AB86', 
      lineWidth: 1.5,
      autoscaleInfoProvider: fixedScaleProvider,
      priceLineVisible: false,
      lastValueVisible: false,
      visible: visibility.sma20,
    });
    seriesRefs.current.sma20 = sma20Series;

    const sma50Series = chart.addSeries(LineSeries, {
      color: '#C3728E', 
      lineWidth: 1.5,
      autoscaleInfoProvider: fixedScaleProvider,
      priceLineVisible: false,
      lastValueVisible: false,
      visible: visibility.sma50,
    });
    seriesRefs.current.sma50 = sma50Series;

    const sma200Series = chart.addSeries(LineSeries, {
      color: '#66B2BB', 
      lineWidth: 1.5,
      autoscaleInfoProvider: fixedScaleProvider,
      priceLineVisible: false,
      lastValueVisible: false,
      visible: visibility.sma200,
    });
    seriesRefs.current.sma200 = sma200Series;

    if (Array.isArray(data) && data.length > 0) {
      const data20 = [];
      const data50 = [];
      const data200 = [];

      data.forEach(item => {
        let safeTimeStr = item.time;
        if (safeTimeStr && safeTimeStr.length === 10) safeTimeStr += "T00:00:00";
        else if (safeTimeStr) safeTimeStr = safeTimeStr.replace(' ', 'T') + ':00';
        else return; 

        const dateObj = new Date(safeTimeStr);
        let timestamp = Math.floor(dateObj.getTime() / 1000);
        timestamp = timestamp - (dateObj.getTimezoneOffset() * 60);

        if (!isNaN(timestamp)) {
          data20.push({ time: timestamp, value: item['%above20'] || 0 });
          data50.push({ time: timestamp, value: item['%above50'] || 0 });
          data200.push({ time: timestamp, value: item['%above200'] || 0 });
        }
      });

      data20.sort((a, b) => a.time - b.time);
      data50.sort((a, b) => a.time - b.time);
      data200.sort((a, b) => a.time - b.time);

      if (data20.length > 0) sma20Series.setData(data20);
      if (data50.length > 0) sma50Series.setData(data50);
      if (data200.length > 0) sma200Series.setData(data200);

      chart.timeScale().fitContent();
    }

    chartRef.current = chart;

    const handleResize = () => {
      if (chartContainerRef.current && chartRef.current) {
        chartRef.current.applyOptions({ width: chartContainerRef.current.clientWidth });
      }
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      if (chartRef.current) {
        chartRef.current.remove();
        chartRef.current = null;
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, timeframe]); 

  const toggleVisibility = (seriesName) => {
    setVisibility(prev => ({ ...prev, [seriesName]: !prev[seriesName] }));
  };

  return (
    <div style={{ 
      background: '#000000', 
      border: '1px solid #111111', 
      borderRadius: '10px', 
      boxShadow: '0 18px 40px #0000002e',
      overflow: 'hidden',
      padding: '15px'
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
        
        <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
          
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <h3 style={{ 
              margin: 0, 
              color: '#e2e8f0', 
              fontSize: '13px', 
              fontWeight: '700', 
              letterSpacing: '0.06em', 
              textTransform: 'uppercase' 
            }}>
              Market Breadth - {timeframe}
            </h3>
            {loadingTime && (
              <span style={{ 
                fontSize: '0.7rem', color: '#4197F7', background: 'rgba(65,151,247,0.14)', 
                padding: '2px 6px', borderRadius: '6px', border: '1px solid rgba(65,151,247,0.3)', fontWeight: 'bold'
              }}>
                ⏱️ {Number(loadingTime).toFixed(2)}s
              </span>
            )}
            {/* NEW: Last Loaded Timestamp Badge */}
            {lastLoaded && (
              <span style={{ 
                fontSize: '0.7rem', color: '#94a3b8', background: 'rgba(148,163,184,0.1)', 
                padding: '2px 6px', borderRadius: '6px', border: '1px solid rgba(148,163,184,0.2)', fontWeight: '600'
              }}>
                ↻ {lastLoaded}
              </span>
            )}
          </div>
          
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ fontSize: '0.75rem', color: '#94a3b8', fontWeight: '600' }}>FRAMES:</span>
            <input 
              type="number" 
              value={framesInput}
              onChange={(e) => setFramesInput(parseInt(e.target.value) || '')}
              style={{ 
                width: '60px', padding: '4px 8px', borderRadius: '6px', 
                border: '1px solid #1a1a1a', background: '#0a0a0a', color: '#e2e8f0',
                fontSize: '0.85rem', outline: 'none'
              }}
            />
            <button 
              onClick={() => onFetchFrames(framesInput)}
              style={{ 
                padding: '4px 14px', 
                background: 'linear-gradient(90deg, #4196f769 -94.84%, #c872f380 162.4%)', 
                color: '#fff', border: 'none', borderRadius: '6px', 
                cursor: 'pointer', fontWeight: '600', fontSize: '0.8rem',
                boxShadow: '#8765f61f 0 0 0 1px'
              }}
            >
              LOAD
            </button>
          </div>
        </div>

        <div style={{ display: 'flex', gap: '15px', fontSize: '0.85rem', fontWeight: '600' }}>
          <span 
            onClick={() => toggleVisibility('sma20')}
            style={{ color: '#84AB86', cursor: 'pointer', userSelect: 'none', opacity: visibility.sma20 ? 1 : 0.4, transition: 'opacity 0.2s' }}
          >
            % &gt; SMA 20
          </span>
          <span 
            onClick={() => toggleVisibility('sma50')}
            style={{ color: '#C3728E', cursor: 'pointer', userSelect: 'none', opacity: visibility.sma50 ? 1 : 0.4, transition: 'opacity 0.2s' }}
          >
            % &gt; SMA 50
          </span>
          <span 
            onClick={() => toggleVisibility('sma200')}
            style={{ color: '#66B2BB', cursor: 'pointer', userSelect: 'none', opacity: visibility.sma200 ? 1 : 0.4, transition: 'opacity 0.2s' }}
          >
            % &gt; SMA 200
          </span>
        </div>
      </div>
      
      <div style={{ position: 'relative', width: '100%', height: '400px', backgroundColor: '#000000', borderRadius: '8px', overflow: 'hidden', border: '1px solid #111111' }}>
        <div ref={chartContainerRef} className="chart-container" style={{ width: '100%', height: '100%' }} />
        
        {(!Array.isArray(data) || data.length === 0) && (
          <div style={{ 
            position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, 
            display: 'flex', alignItems: 'center', justifyContent: 'center', 
            backgroundColor: 'rgba(0,0,0,0.8)', color: '#64748b', zIndex: 10,
            fontSize: '0.9rem', fontWeight: '600', letterSpacing: '0.05em'
          }}>
            {typeof data === 'string' ? data : `NO DATA AVAILABLE FOR ${timeframe.toUpperCase()}`}
          </div>
        )}
      </div>
    </div>
  );
};

export default ChartCard;